import { useEffect, useMemo } from "react"
import { featureCollection } from "@turf/helpers"
import { get } from "lodash"

import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { VenueData } from "providers/venue/types"

import { getSuitablyValueBetweenBearings } from "components/IndoorMap"
import { HOMEPAGE_ANIMATION } from "constant"

export const useHomeMap = (venues: VenueData[]) => {
  const { dataLoaded, mapConfig } = useVenue()
  const { indoorRef, mapLoaded, defaultBearing } = useIndoorMap()

  const mapReady = indoorRef?.current && mapLoaded && dataLoaded

  const allVenuesViewConfig = useMemo(
    () => get(mapConfig, "all_venues_view", {}),
    [mapConfig]
  )

  useEffect(() => {
    const indoorMap = indoorRef.current
    if (mapReady && indoorMap) {
      indoorMap.showVenueObjects()
      const currentBearing = indoorMap.getBearing()

      const { zoom: targetZoom } = allVenuesViewConfig

      const bearing = getSuitablyValueBetweenBearings(
        defaultBearing,
        currentBearing
      )
      const venuesCollection = featureCollection(venues)

      const extent = indoorMap.getFeatureExtent(venuesCollection)

      const center = indoorMap.getExtentCenter(extent)

      const zoom = targetZoom || indoorMap.getExtentZoom(extent)

      const { enable: enableAnimation = true, speed: animationSpeed = 0.3 } =
        get(mapConfig, `animations.${HOMEPAGE_ANIMATION}`, {})

      indoorMap.flyTo(center, {
        zoom,
        bearing,
      })

      indoorMap.clearAnimations()

      if (enableAnimation) {
        indoorMap.addAnimations({
          id: HOMEPAGE_ANIMATION,
          callback: () => {
            const currentBearing = indoorMap.getBearing()
            const newBearing = (currentBearing + animationSpeed) % 360
            indoorMap.setBearing(newBearing)
          },
        })
      }

      indoorMap.changeLevelByOrdinal(0)
      indoorMap.clearHighlightElements()
      indoorMap.clearHighlightObject()
      indoorMap.enableLayersFadingOnZoom()
    }
    return () => {
      if (indoorMap) {
        indoorMap.removeAnimationById(HOMEPAGE_ANIMATION)
        indoorMap.disableLayersFadingOnZoom()
      }
    }
  }, [
    defaultBearing,
    indoorRef,
    mapConfig,
    mapReady,
    venues,
    allVenuesViewConfig,
  ])
}
